import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PIPING_OPTIONS } from '@/components/OptionsStep';

const ReviewStep = ({
  cushionName,
  productType,
  productTypeLabel,
  dimensionsSummary,
  fabricColorForPreview,
  displayFabricName,
  selectedFoamName,
  pipingOption,
  pipingCost = 0,
  pipingFabricName,
  quantity,
  setQuantity,
  priceInfo,
  extraRows = [],
}) => (
  <div data-testid="step-review">
    <h2 className="text-lg font-semibold text-[#1A1A1A] mb-2">Review Your Order</h2>
    <p className="text-gray-600 mb-4 sm:mb-8 text-sm">Confirm your selections and add to cart.</p>
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-sand rounded-xl p-4 sm:p-6 space-y-3 sm:space-y-4">
        <div className="flex justify-between text-sm sm:text-base">
          <span className="text-gray-600">Cushion Type</span>
          <span className="font-semibold">{cushionName}</span>
        </div>
        <div className="flex justify-between text-sm sm:text-base">
          <span className="text-gray-600">Product Type</span>
          <span className="font-semibold">{productTypeLabel}</span>
        </div>
        <div className="flex justify-between text-sm sm:text-base">
          <span className="text-gray-600">Dimensions</span>
          <span className="font-semibold">{dimensionsSummary}</span>
        </div>
        {extraRows.map((row, i) => (
          <div key={i} className="flex justify-between text-sm sm:text-base">
            <span className="text-gray-600">{row.label}</span>
            <span className="font-semibold">{row.value}</span>
          </div>
        ))}
        {productType !== 'foam_only' && (
          <div className="flex justify-between text-sm sm:text-base">
            <span className="text-gray-600">Fabric</span>
            <div className="flex items-center gap-2">
              <span className="font-semibold">{displayFabricName}</span>
            </div>
          </div>
        )}
        {productType !== 'cover_only' && selectedFoamName && (
          <div className="flex justify-between text-sm sm:text-base">
            <span className="text-gray-600">Foam</span>
            <span className="font-semibold">{selectedFoamName}</span>
          </div>
        )}
        {pipingOption !== 'none' && (
          <div className="flex justify-between text-sm sm:text-base">
            <span className="text-gray-600">Piping</span>
            <span className="font-semibold">
              {PIPING_OPTIONS.find(o => o.value === pipingOption)?.label} (+${pipingCost.toFixed(0)})
              {pipingOption === 'different' && pipingFabricName && (
                <span className="block text-xs text-gray-500 font-normal text-right">{pipingFabricName}</span>
              )}
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-base sm:text-lg font-semibold">Quantity</Label>
        <div className="flex items-center gap-3 sm:gap-4">
          <Button variant="outline" size="icon" onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1} data-testid="quantity-decrease">
            <Minus className="h-4 w-4" />
          </Button>
          <span className="text-xl sm:text-2xl font-bold w-10 sm:w-12 text-center" data-testid="quantity-value">{quantity}</span>
          <Button variant="outline" size="icon" onClick={() => setQuantity(quantity + 1)} data-testid="quantity-increase">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {priceInfo && (
        <div className="border-t pt-4 sm:pt-6 space-y-2 sm:space-y-3" data-testid="price-breakdown">
          <div className="flex justify-between text-gray-600 text-sm sm:text-base"><span>Base Price</span><span>${priceInfo.base_price.toFixed(0)}</span></div>
          <div className="flex justify-between text-gray-600 text-sm sm:text-base"><span>Dimension Cost</span><span>${priceInfo.dimension_cost.toFixed(0)}</span></div>
          {productType !== 'foam_only' && (
            <div className="flex justify-between text-gray-600 text-sm sm:text-base"><span>Fabric Premium</span><span>${priceInfo.fabric_premium.toFixed(0)}</span></div>
          )}
          {productType !== 'cover_only' && (
            <div className="flex justify-between text-gray-600 text-sm sm:text-base"><span>Foam Cost</span><span>${priceInfo.foam_cost.toFixed(0)}</span></div>
          )}
          {pipingCost > 0 && (
            <div className="flex justify-between text-gray-600 text-sm sm:text-base"><span>Piping</span><span>${pipingCost.toFixed(0)}</span></div>
          )}
          <div className="flex justify-between text-base sm:text-lg pt-2 sm:pt-3 border-t"><span className="font-semibold">Unit Price</span><span className="font-bold">${(priceInfo.unit_price + pipingCost).toFixed(0)}</span></div>
          <div className="flex justify-between text-xl sm:text-2xl font-bold text-bush"><span>Total</span><span data-testid="total-price">${((priceInfo.unit_price + pipingCost) * quantity).toFixed(0)}</span></div>
        </div>
      )}
    </div>
  </div>
);

export default ReviewStep;
